import { useCallback, useEffect, useMemo, useState } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";

import { useMutation, useQuery } from "@apollo/client";
import { 
  ADD_USER_EMOJI_PACK,
  DELETE_USER_EMOJI_PACK,
  RESET_EMOJI_PACK_TUTOR,
  RESET_USER_DAILY_EMOJIS,
  UPDATE_USER_EMOJI_PACK,
  USER_EMOJI_PACKS
} from "../../../graphql";
import { 
  AddUserEmojiPackMutation,
  AddUserEmojiPackMutationVariables,
  DeleteUserEmojiPackMutation,
  DeleteUserEmojiPackMutationVariables,
  EmojiPackLayout,
  GetEmojiPacksQuery,
  GetEmojiPacksQueryVariables,
  ResetDailyEmojisMutation,
  ResetDailyEmojisMutationVariables,
  ResetEmojiPackTutorialMutation,
  ResetEmojiPackTutorialMutationVariables,
  UpdateUserEmojiPackMutation,
  UpdateUserEmojiPackMutationVariables,
  UserEmojiPacksQuery,
  UserEmojiPacksQueryVariables,
  UserSearchType
} from "../../../../../../__gqltypes__";

import { LoadingLogo } from "../../../components/Modal";
import DeleteItemModal from "../../../../liveops/screens/Items/components/DeleteItemModal";
import { GET_EMOJI_PACKS } from "../../../../liveops/components/apolloQueries";

type UserEmojiPackData = NonNullable<Required<UserEmojiPacksQuery>['user']>['emojiPacks']['list'][0];
type EmojiData = UserEmojiPackData['emojiPack']['emojis'][0];

type EmojiPreviewProps = {
  myIndex: number;
  isAvailable: boolean;
  onSelect: (myIndex: number, val: boolean) => void;
  data: EmojiData;
  disabled: boolean;
}

function EmojiPrevieWithCheck({myIndex, isAvailable, onSelect, data, disabled}: EmojiPreviewProps): JSX.Element {
  const [selected, setSelected] = useState(isAvailable);
  const toggleSelected = () => {
    setSelected(!selected);
    onSelect(myIndex, !selected);
  };

  useEffect(() => {
    setSelected(isAvailable);
  }, [isAvailable]);

  return (
    <div style={styles.previewCont}>
      <img src={data.asset} alt={data.name} style={{...styles.img, opacity: selected ? 1 : 0.6}} />
      <Form.Check
        type="checkbox"
        checked={selected}
        onChange={toggleSelected}
        label=""
        style={styles.checkbox}
        disabled={disabled}
        />
    </div>
  );
}

type EmojiPackViewProps = {
  userId: string;
  data: UserEmojiPackData | void;
  isOwn: boolean;
};

function UserEmojiPackView({userId, data, isOwn}:EmojiPackViewProps): JSX.Element {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [availableEmojis, setAvailableEmojis] = useState<Array<boolean>>([]);

  const isPaidPack = data ? data.emojiPack.layout === EmojiPackLayout.IAP : false;

  const [addUserEmojiPack, { loading: addLoading }] = useMutation<
    AddUserEmojiPackMutation,
    AddUserEmojiPackMutationVariables>(ADD_USER_EMOJI_PACK);

  const [updateUserEmojiPack, { loading: updateLoading }] = useMutation<
    UpdateUserEmojiPackMutation,
    UpdateUserEmojiPackMutationVariables
  >(UPDATE_USER_EMOJI_PACK);

  const [deleteUserEmojiPack, { loading: deleteLoading }] = useMutation<
    DeleteUserEmojiPackMutation,
    DeleteUserEmojiPackMutationVariables
  >(DELETE_USER_EMOJI_PACK);

  const handleAdd = useCallback(() => {
    if (!data) {
      return;
    }
    addUserEmojiPack({
      variables: {
        input: {
          userId,
          packId: data.emojiPack.id,
        }
      }
    });
  }, [addUserEmojiPack, data, userId]);

  const handleDelete = useCallback(() => {
    setShowDeleteModal(false);
    deleteUserEmojiPack({variables: {
      input: {
        userId,
        packId: data ? data.emojiPack.id : '',
      },
    }});
  }, [deleteUserEmojiPack, userId, data]);

  const handleSave = useCallback(() => {
    updateUserEmojiPack({
      variables: {
        input: {
          userId,
          packId: data ? data.emojiPack.id : '',
          availableEmojis,
        }
      }
    });
  }, [updateUserEmojiPack, userId, data, availableEmojis]);

  const handleChangeAvailable = useCallback((newAvai: boolean[], allAvailable = false) => {
    const newAvailable = availableEmojis.map(val => val);
    if (allAvailable) {
      setAvailableEmojis(newAvailable.fill(true));
      return;
    }

    if (newAvai.length === 0) {
      setAvailableEmojis(newAvailable.fill(false));
      return;
    }
    setAvailableEmojis(newAvai);
  }, [availableEmojis]);

  
  const onSelected = useCallback((index: number, value: boolean) => {
    const newAvai = availableEmojis.map(val => val);
    newAvai[index] = value;
    handleChangeAvailable(newAvai);
  }, [availableEmojis, handleChangeAvailable]);

  useEffect(() => {
    if (data) {
      setAvailableEmojis(data.availability.map((val) => val.isAvailable));
    }
  }, [data]);

  const emojiPackData = data?.emojiPack;
  const chunkSize = isPaidPack ? 5 : 10;
  const emojiRows: Array<Array<EmojiData>> = useMemo(() => {
    if (!emojiPackData) {
      return [];
    }
    
    const numOfRows = emojiPackData.emojis.length / chunkSize;
    const rows: Array<Array<EmojiData>> = new Array(numOfRows);
    emojiPackData.emojis.forEach((emoji, index) => {
      const rowIndex = Math.floor(index / chunkSize);
      if (!rows[rowIndex]) {
        rows[rowIndex] = [emoji];
      } else {
        rows[rowIndex].push(emoji);
      }
    });
    return rows;
  }, [chunkSize, emojiPackData]);

  if (!emojiPackData) {
    return <></>;
  }
 
  const loading = updateLoading || deleteLoading || addLoading;

  if (loading) {
    return <LoadingLogo show={true} />;
  }

  return (
    <Card>
      <DeleteItemModal
        title={`Delete "${emojiPackData.name}" EmojiPack for the user?`}
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
      />
      <Card.Body>
        <Row className="align-items-center">
          {isOwn ? (
            <>
            <Form.Group as={Col} xs="auto">
              <Button className="mr-1" onClick={handleSave}>
                <i className="fas fa-save"/> Save
              </Button>
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Button className="mr-1" variant="danger" onClick={() => setShowDeleteModal(true)}>
                <i className="fas fa-trash"/> Delete
              </Button>
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Button className="mr-1" onClick={() => handleChangeAvailable([])}>
                <i className="fas fa-broom"/> Reset
              </Button>
            </Form.Group>
            <Form.Group as={Col} xs="auto">
              <Button className="mr-1" onClick={() => handleChangeAvailable([], true)}>
                <i className="fas fa-thermometer-full"/> All
              </Button>
            </Form.Group>
          </>) : (
            <>
              <Form.Group as={Col} xs="auto">
                <Form.Label>User does not own this emoji pack. Would you like to add it?</Form.Label>
              </Form.Group>
              <Form.Group as={Col} xs="auto">
                <Button className="mr-1" onClick={handleAdd}>
                  <i className="fas fa-plus"/> Add
                </Button>
            </Form.Group>
          </>
          )}
        </Row>
        {emojiRows.map((row, rowIdx) => {
            return (
              <Row key={`row-${rowIdx}`} className="mb-4 ml-0">
                {row.map((emoji, emojiIdx) => {
                  const myIndex = rowIdx * chunkSize + emojiIdx;
                  return (
                    <EmojiPrevieWithCheck 
                      data={emoji}
                      myIndex={myIndex}
                      isAvailable={availableEmojis[myIndex]}
                      onSelect={onSelected}
                      disabled={!isOwn}
                      />
                  );
                })}
              </Row>
            );
          })}
      </Card.Body>
    </Card>
  );
}

type Props = {
  userId: string;
}

export function UserEmojiPackDisplay({userId}: Props): JSX.Element {
  const none = 'none';
  const [expanded, setExpanded] = useState(false);
  const [currentPackId, setCurrentPackId] = useState(none);
  const {data, loading: userEmojiPacksLoading} = useQuery<
    UserEmojiPacksQuery,
    UserEmojiPacksQueryVariables
  >(USER_EMOJI_PACKS, {
    variables: {
      property: userId,
      type: UserSearchType.BY_ID,
      limit: null,
      offset: 0,
    },
    skip: !userId,
  });

  const {data: allEmojiPacksData, loading: allEmojiPacksLoading} = useQuery<
      GetEmojiPacksQuery,
      GetEmojiPacksQueryVariables
    >(GET_EMOJI_PACKS);
  
  const [resetTutor, { loading: resetLoading }] = useMutation<
    ResetEmojiPackTutorialMutation,
    ResetEmojiPackTutorialMutationVariables
  >(RESET_EMOJI_PACK_TUTOR);

  const [resetDailyEmojis, { loading: resetDELoading }] = useMutation<
    ResetDailyEmojisMutation,
    ResetDailyEmojisMutationVariables
  >(RESET_USER_DAILY_EMOJIS);

  const allEmojiPacks = useMemo(() => 
    allEmojiPacksData?.liveOps.emojiPacks.list ?? [],
    [allEmojiPacksData?.liveOps.emojiPacks.list]
  );

  const idToName = useMemo(() => {
    const res = new Map<string, string>();
    res.set(none, none);
    allEmojiPacks.forEach((ep) => {
      res.set(ep.id, ep.name);
    });
    return res;
  }, [allEmojiPacks]);

  const allEmojiPackIds: string[] = useMemo(() => {
    if (allEmojiPacks.length > 0) {
      return [none].concat(allEmojiPacks.map((val) => val.id));
    }
    return [none];
  }, [allEmojiPacks]);

  const ownedEmojiPacks = useMemo(() => {
    if (!data || !data.user) {
      return new Set<string>();
    }

    return new Set<string>(data.user.emojiPacks.list.map((val) => val.emojiPack.id).concat(none));
  }, [data]);

  const emojiPackIdToData: Map<string, UserEmojiPackData> = useMemo(() => {
    if (!data || !data.user) {
      return new Map<string, UserEmojiPackData>();
    }
    const result = new Map<string, UserEmojiPackData>(
      data.user.emojiPacks.list.map((val) => ([val.emojiPack.id, val])));

      allEmojiPacks.forEach((ep) => {
      if (!ownedEmojiPacks.has(ep.id)) {
        result.set(ep.id, {
          __typename: 'UserEmojiPack',
          emojiPack: ep,
          isActive: false,
          availability: new Array(ep.emojis.length).fill(false),
        });
      }
    });

    return result;
  }, [allEmojiPacks, data, ownedEmojiPacks]);

  const currentPack = useMemo(() => {
    return emojiPackIdToData.get(currentPackId);
  }, [emojiPackIdToData, currentPackId]);

  const handleReset = useCallback(() => {
    resetTutor({
      variables: {
        userId,
      },
    });
  }, [resetTutor, userId]);

  const handleResetDE = useCallback(() => {
    resetDailyEmojis({
      variables: {
        userId,
      },
    });
  }, [resetDailyEmojis, userId]);

  const loading = userEmojiPacksLoading || resetLoading || allEmojiPacksLoading || resetDELoading;
  
  return (
    <Card className="mb-4">
      <Card.Body>
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(value => !value)}>
            <div
              style={{
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              User Emoji Packs
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            {loading ? (
              <LoadingLogo show={loading} />
            ) : (
              <>
                <Row>
                  <Form.Group as={Col} xs="auto">
                    <Form.Control 
                      as="select"
                      value={currentPackId}
                      onChange={(e) => setCurrentPackId(e.target.value)}>
                      {allEmojiPackIds.map((id) => (
                        <option value={id} key={id}>
                          {`${!ownedEmojiPacks.has(id) ? '- ' : ''} ${idToName.get(id)}`}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group as={Col} xs="auto">
                    <Button className="mr-1" onClick={handleReset}>
                    <i className="fas fa-broom"/> Reset Tutorial
                    </Button>
                  </Form.Group>
                  <Form.Group as={Col} xs="auto">
                    <Button className="mr-1" onClick={handleResetDE}>
                    <i className="fas fa-battery-empty"/> Reset Daily Emojis
                    </Button>
                  </Form.Group>
                </Row>
                {expanded && <UserEmojiPackView 
                  userId={userId}
                  data={currentPack}
                  isOwn={ownedEmojiPacks.has(currentPackId)}/>}
              </>
            )} 
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}

const styles: {[key:string]: React.CSSProperties } = {
  previewCont: {
    marginRight: 4,
    position: 'relative',
    display: 'inline-block',
    cursor: 'pointer',
  },
  img: {
    display: 'block',
    width: 48,
    height: 48,
    transition: 'opacity 0.3s ease',
  },
  checkbox: {
    position: 'absolute',
    bottom: '-4px',
    right: '-4px',
    borderRadius: '4px'
  },
};